/** Canonical current-attempt presentation for Canvas media generation nodes. */

export type GenerationPresentationPhase =
  | 'NO_ATTEMPT'
  | 'SUBMITTING'
  | 'QUEUED'
  | 'GENERATING'
  | 'SUCCEEDED'
  | 'FAILED'
  | 'SYNC_ERROR'

export interface CurrentGenerationPresentation {
  phase: GenerationPresentationPhase
  /** Label/badge status — never FAILED while a new attempt is busy. */
  displayStatus: string
  showSpinner: boolean
  showFailedResult: boolean
  showEmptyResult: boolean
  showFooterError: boolean
  showSyncError: boolean
}

/** Map hook/node inputs to a single current-attempt presentation. */
export function deriveCurrentGenerationPresentation(args: {
  effectiveStatus: string
  isBusy: boolean
  runHydrating: boolean
  /** Persisted output id on the node — not rendered image URL. */
  hasPersistedOutput: boolean
  taskTerminalFailed: boolean
  taskTerminalSucceeded: boolean
  /** Node has a persisted current-attempt task id (latest_run_id). */
  hasAttemptIdentity: boolean
  pollSyncError: boolean
}): CurrentGenerationPresentation {
  const {
    effectiveStatus,
    isBusy,
    runHydrating,
    hasPersistedOutput,
    taskTerminalFailed,
    taskTerminalSucceeded,
    hasAttemptIdentity,
    pollSyncError,
  } = args
  const executionActive = isBusy || runHydrating

  let displayStatus = effectiveStatus
  if (executionActive) {
    if (effectiveStatus === 'QUEUED' || effectiveStatus === 'WAITING_FOR_ENGINE') {
      displayStatus = effectiveStatus === 'WAITING_FOR_ENGINE' ? 'WAITING_FOR_ENGINE' : 'QUEUED'
    } else if (
      effectiveStatus === 'UPLOADING_ASSETS' ||
      effectiveStatus === 'SUBMITTING_REQUEST' ||
      effectiveStatus === 'RETRYING' ||
      effectiveStatus === 'GENERATING'
    ) {
      displayStatus = effectiveStatus
    } else {
      displayStatus = 'SUBMITTING'
    }
  }

  let phase: GenerationPresentationPhase = 'NO_ATTEMPT'
  if (pollSyncError && hasAttemptIdentity && !taskTerminalFailed && !taskTerminalSucceeded) {
    phase = 'SYNC_ERROR'
    displayStatus = 'SYNC_ERROR'
  } else if (executionActive) {
    if (displayStatus === 'QUEUED' || displayStatus === 'WAITING_FOR_ENGINE') phase = 'QUEUED'
    else if (
      displayStatus === 'UPLOADING_ASSETS' ||
      displayStatus === 'SUBMITTING_REQUEST' ||
      displayStatus === 'RETRYING' ||
      displayStatus === 'GENERATING'
    ) {
      phase = 'GENERATING'
    } else phase = 'SUBMITTING'
  } else if (effectiveStatus === 'FAILED' || taskTerminalFailed) {
    phase = 'FAILED'
    displayStatus = 'FAILED'
  } else if (
    effectiveStatus === 'COMPLETED' ||
    hasPersistedOutput ||
    (taskTerminalSucceeded && hasAttemptIdentity)
  ) {
    phase = 'SUCCEEDED'
    displayStatus = 'COMPLETED'
  } else if (hasAttemptIdentity && !taskTerminalFailed) {
    // Task exists but fields not yet hydrated — keep attempt alive, not NO_ATTEMPT.
    phase = executionActive ? 'SUBMITTING' : 'GENERATING'
    displayStatus = phase === 'GENERATING' ? 'GENERATING' : 'SUBMITTING'
  } else {
    phase = 'NO_ATTEMPT'
    displayStatus = 'READY'
  }

  const showSpinner =
    phase === 'SUBMITTING' || phase === 'QUEUED' || phase === 'GENERATING' || phase === 'SYNC_ERROR'
  const showFailedResult = phase === 'FAILED'
  const showEmptyResult = phase === 'NO_ATTEMPT'
  const showFooterError = phase === 'FAILED' || phase === 'SYNC_ERROR'
  const showSyncError = phase === 'SYNC_ERROR'

  return {
    phase,
    displayStatus,
    showSpinner,
    showFailedResult,
    showEmptyResult,
    showFooterError,
    showSyncError,
  }
}
